import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';

@observer
class BulkGenerateStages extends Component {
    constructor(props) {
        super(props);
        const { conventionStore } = props.stores;
        const { convention } = conventionStore;

        // Default start time to convention start date or now
        const defaultStartTime = convention.startDate
            ? util.isoDate(convention.startDate)
            : util.isoDate(new Date());

        // Default end time to 2 hours after start
        const startDate = new Date(convention.startDate || new Date());
        const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
        const defaultEndTime = util.isoDate(endDate);

        // Default interval from convention or 10 minutes
        const defaultInterval = convention.stageInterval || 10;

        this.state = {
            type: 'felt',
            startTime: defaultStartTime,
            endTime: defaultEndTime,
            intervalMinutes: defaultInterval,
            loading: false,
            error: null,
        };
    }

    handleTypeChange = (e) => {
        this.setState({ type: e.target.value, error: null });
    }

    handleStartTimeChange = (e) => {
        this.setState({ startTime: e.target.value, error: null });
    }

    handleEndTimeChange = (e) => {
        this.setState({ endTime: e.target.value, error: null });
    }

    handleIntervalChange = (e) => {
        this.setState({ intervalMinutes: parseInt(e.target.value, 10) || 0, error: null });
    }

    handleGenerate = async () => {
        const { conventionStore, appState } = this.props.stores;
        const { callback } = this.props;
        const { type, startTime, endTime, intervalMinutes } = this.state;

        this.setState({ loading: true, error: null });

        try {
            await conventionStore.bulkGenerateStages({
                type,
                startTime,
                endTime,
                intervalMinutes,
            });

            appState.addToast({
                type: 'success',
                message: `Successfully generated ${this.getPreviewCount()} stages`,
            }, 5000);

            // Close drawer and refresh
            if (callback) {
                await callback();
            }
            appState.closeDrawer();
        } catch (err) {
            this.setState({ error: err.message });
            appState.addToast({
                type: 'error',
                message: `Failed to generate stages: ${err.message}`,
            }, 5000);
        } finally {
            this.setState({ loading: false });
        }
    }

    getPreviewCount = () => {
        const { conventionStore } = this.props.stores;
        const { startTime, endTime, intervalMinutes } = this.state;
        return conventionStore.calculateStagePreview({ startTime, endTime, intervalMinutes });
    }

    render() {
        const { type, startTime, endTime, intervalMinutes, loading, error } = this.state;
        const { appState, userStore, conventionStore } = this.props.stores;
        const { user = {} } = userStore;
        const { convention } = conventionStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const previewCount = this.getPreviewCount();
        const existingStageCount = convention.stages?.length || 0;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class={`font-weight-lighter px-3 mt-3 box-header w-100 position-relative`}>
                        <Text id='convention.bulk-generate-stages'>Bulk Generate Stages</Text>
                    </div>

                    <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex flex-column'>
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.convention-name'>Convention</Text>
                            </span>
                            <span class='font-weight-bold'>{convention.title}</span>
                            <small class='text-muted'>
                                <Text id='convention.existing-stages'>Existing stages</Text>: {existingStageCount}
                            </small>
                        </div>

                        <div class='d-flex flex-column mt-4'>
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.stage-type'>Stage type</Text>
                            </span>
                            <select
                                class='form-control rounded-pill'
                                value={type}
                                onInput={this.handleTypeChange}
                            >
                                <option value='nais'>NAIS</option>
                                <option value='hurtig'>Hurtig</option>
                                <option value='hurtig-half'>Hurtig halv serie</option>
                                <option value='standard'>Standard</option>
                                <option value='felt'>Felt</option>
                                <option value='norgesfelt'>Norgesfelt</option>
                                <option value='t-96'>T-96</option>
                                <option value='finpistol'>VM-Finpistol</option>
                                <option value='grovpistol'>VM-Grovpistol</option>
                                <option value='silhuett'>Silhuettpistol</option>
                                <option value='luftpistol'>Luftpistol 10m</option>
                                <option value='skarpskytter'>Skarpskyttermerke</option>
                                <option value='tidsoving'>Tidsoving</option>
                                <option value='tidskonk'>Tidskonkurranse</option>
                                <option value='fri'>Fri (poeng)</option>
                            </select>
                        </div>

                        <div class='d-flex flex-column mt-3'>
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.start-time'>Start time</Text>
                            </span>
                            <input
                                class='form-control rounded-pill'
                                type='datetime-local'
                                value={startTime}
                                onInput={this.handleStartTimeChange}
                            />
                        </div>

                        <div class='d-flex flex-column mt-3'>
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.end-time'>End time</Text>
                            </span>
                            <input
                                class='form-control rounded-pill'
                                type='datetime-local'
                                value={endTime}
                                onInput={this.handleEndTimeChange}
                            />
                        </div>

                        <div class='d-flex flex-column mt-3'>
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.interval-minutes'>Interval (minutes)</Text>
                            </span>
                            <input
                                class='form-control rounded-pill'
                                type='number'
                                min='1'
                                value={intervalMinutes}
                                onInput={this.handleIntervalChange}
                            />
                        </div>

                        {/* Preview */}
                        <div class={`d-flex flex-column mt-4 p-3 rounded-lg ${previewCount > 0 ? 'bg-success text-white' : 'bg-warning'}`}>
                            <div class='d-flex justify-content-between align-items-center'>
                                <span class='font-weight-light'>
                                    <Text id='convention.stages-to-generate'>Stages to generate</Text>:
                                </span>
                                <span class='font-weight-bold' style='font-size: 1.5em;'>
                                    {previewCount}
                                </span>
                            </div>
                            {previewCount > 0 && (
                                <small>
                                    <Text id='convention.stages-numbered'>Stages will be numbered</Text> {existingStageCount + 1} - {existingStageCount + previewCount}
                                </small>
                            )}
                        </div>

                        {error && (
                            <div class='alert alert-danger mt-3 rounded-lg'>
                                {error}
                            </div>
                        )}

                        <div class='d-flex justify-content-center mt-4'>
                            <button
                                class='btn btn-lg btn-success rounded-pill px-5'
                                onClick={this.handleGenerate}
                                disabled={loading || previewCount === 0}
                            >
                                {loading ? (
                                    <>
                                        <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                                        <Text id='convention.generating'>Generating...</Text>
                                    </>
                                ) : (
                                    <>
                                        <i class='fa-duotone fa-layer-plus me-2' />
                                        <Text id='convention.generate-stages'>Generate {previewCount} Stages</Text>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default BulkGenerateStages;
